import type { SVGProps } from 'react';

export type IconName =
  | 'annotation-arrow'
  | 'annotation-callout'
  | 'annotation-ellipse'
  | 'annotation-focus'
  | 'annotation-marker'
  | 'annotation-polygon'
  | 'annotation-rectangle'
  | 'annotation-text'
  | 'arrow-down'
  | 'arrow-up'
  | 'brand'
  | 'check'
  | 'chevron-down'
  | 'chevron-right'
  | 'close'
  | 'collapse'
  | 'expand'
  | 'eye'
  | 'eye-off'
  | 'drag'
  | 'download'
  | 'fit'
  | 'help'
  | 'layers'
  | 'lock'
  | 'align-left'
  | 'align-centre'
  | 'align-right'
  | 'map'
  | 'markup'
  | 'more'
  | 'move'
  | 'plus'
  | 'rename'
  | 'reverse'
  | 'search'
  | 'select'
  | 'select-attribute'
  | 'select-rectangle'
  | 'select-lasso'
  | 'table'
  | 'trash'
  | 'upload'
  | 'zoom';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'name'> {
  readonly name: IconName;
}

export function Icon({ name, ...props }: IconProps) {
  const shared = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  };

  return (
    <svg aria-hidden="true" className="icon" data-icon={name} viewBox="0 0 24 24" {...props}>
      <g {...shared}>
        {name === 'annotation-marker' ? <circle cx="12" cy="12" r="4" /> : null}
        {name === 'annotation-arrow' ? <path d="M3 17 19 6m0 0-2 6m2-6-6 1" /> : null}
        {name === 'annotation-polygon' ? <path d="m5 17 2-11 10-2 3 10-8 6Z" /> : null}
        {name === 'annotation-rectangle' ? <rect height="12" width="16" x="4" y="6" /> : null}
        {name === 'annotation-ellipse' ? <ellipse cx="12" cy="12" rx="8" ry="5.5" /> : null}
        {name === 'annotation-text' ? <path d="M5 5h14M12 5v14M8 19h8" /> : null}
        {name === 'annotation-callout' ? (
          <>
            <rect height="9" rx="1" width="13" x="3" y="3" />
            <path d="m16 10 5 10m0 0-4-3m4 3v-5" />
          </>
        ) : null}
        {name === 'annotation-focus' ? (
          <>
            <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
            <circle cx="12" cy="12" r="3" />
          </>
        ) : null}
        {name === 'align-left' ? <path d="M4 6h16M4 10h11M4 14h16M4 18h9" /> : null}
        {name === 'align-centre' ? <path d="M4 6h16M6.5 10h11M4 14h16M7.5 18h9" /> : null}
        {name === 'align-right' ? <path d="M4 6h16M9 10h11M4 14h16M11 18h9" /> : null}
        {name === 'arrow-down' ? <path d="M12 4v16m0 0-6-6m6 6 6-6" /> : null}
        {name === 'arrow-up' ? <path d="M12 20V4m0 0L6 10m6-6 6 6" /> : null}
        {name === 'brand' ? (
          <>
            <path d="M12 2.8 20 7.4v9.2L12 21.2 4 16.6V7.4Z" />
            <circle cx="12" cy="12" r="3.1" />
          </>
        ) : null}
        {name === 'check' ? <path d="m5 12 4 4L19 6" /> : null}
        {name === 'chevron-down' ? <path d="m7 9.5 5 5 5-5" /> : null}
        {name === 'chevron-right' ? <path d="m9.5 7 5 5-5 5" /> : null}
        {name === 'close' ? <path d="m7 7 10 10M17 7 7 17" /> : null}
        {name === 'collapse' ? <path d="m14.5 6-6 6 6 6" /> : null}
        {name === 'expand' ? <path d="m9.5 6 6 6-6 6" /> : null}
        {name === 'eye' ? (
          <>
            <path d="M2.8 12s3.3-5 9.2-5 9.2 5 9.2 5-3.3 5-9.2 5-9.2-5-9.2-5Z" />
            <circle cx="12" cy="12" r="2.3" />
          </>
        ) : null}
        {name === 'eye-off' ? (
          <>
            <path d="M3 3 21 21M10.6 7.1A10.6 10.6 0 0 1 12 7c5.9 0 9.2 5 9.2 5a15 15 0 0 1-2.3 2.7M6.2 6.2C4 7.6 2.8 12 2.8 12s3.3 5 9.2 5c1 0 1.9-.1 2.7-.4" />
          </>
        ) : null}
        {name === 'drag' ? (
          <>
            <circle cx="8" cy="6" r="1" fill="currentColor" stroke="none" />
            <circle cx="16" cy="6" r="1" fill="currentColor" stroke="none" />
            <circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="16" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="8" cy="18" r="1" fill="currentColor" stroke="none" />
            <circle cx="16" cy="18" r="1" fill="currentColor" stroke="none" />
          </>
        ) : null}
        {name === 'download' ? (
          <>
            <path d="M12 3v13m0 0 5-5m-5 5-5-5" />
            <path d="M4 15v5h16v-5" />
          </>
        ) : null}
        {name === 'fit' ? (
          <>
            <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
            <path d="m3 8 5-5M21 8l-5-5M3 16l5 5M21 16l-5 5" />
          </>
        ) : null}
        {name === 'layers' ? (
          <>
            <path d="m12 3 9 5-9 5-9-5Z" />
            <path d="m3 12 9 5 9-5M3 16l9 5 9-5" />
          </>
        ) : null}
        {name === 'lock' ? (
          <>
            <rect height="10" rx="1.5" width="14" x="5" y="10" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </>
        ) : null}
        {name === 'map' ? (
          <path d="m3.5 5.5 5-2.5 7 2.5 5-2.5v15.5l-5 2.5-7-2.5-5 2.5Zm5-2.5v15.5m7-13V21" />
        ) : null}
        {name === 'markup' ? (
          <>
            <path d="M5 18.5 7.5 12l7-7 4.5 4.5-7 7Z" />
            <path d="m14.5 5 4.5 4.5M5 18.5l4-1.5-2.5-2.5Z" />
          </>
        ) : null}
        {name === 'more' ? (
          <>
            <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
            <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
          </>
        ) : null}
        {name === 'move' ? (
          <>
            <path d="M12 3v18M3 12h18" />
            <path d="m8 7 4-4 4 4M8 17l4 4 4-4M7 8l-4 4 4 4M17 8l4 4-4 4" />
          </>
        ) : null}
        {name === 'plus' ? <path d="M12 5v14M5 12h14" /> : null}
        {name === 'help' ? (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 2-2.5 2-2.5 4M12 16h.01" />
          </>
        ) : null}
        {name === 'rename' ? (
          <>
            <path d="m4 16-.8 4 4-.8L18.5 7.9l-3.4-3.4Z" />
            <path d="m13.8 5.8 3.4 3.4M10 20h10" />
          </>
        ) : null}
        {name === 'reverse' ? (
          <>
            <path d="M5 7h11l-3-3M19 17H8l3 3" />
            <path d="m16 7-3 3M8 17l3-3" />
          </>
        ) : null}
        {name === 'search' ? (
          <>
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="m15.5 15.5 5 5" />
          </>
        ) : null}
        {name === 'select' ? <path d="m5 3 12 9-6 1.5L8.5 19Z" /> : null}
        {name === 'select-attribute' ? (
          <>
            <rect height="14" rx="1" width="16" x="4" y="5" />
            <path d="M4 10h16M9 10v9M15 10v9" />
          </>
        ) : null}
        {name === 'select-rectangle' ? (
          <>
            <path d="M5 4h4M15 4h4v4M19 14v5h-4M9 19H5v-5M5 9V8" />
          </>
        ) : null}
        {name === 'select-lasso' ? (
          <>
            <path d="M7 17C2 15 2 7 8 5c5-2 12 0 12 5 0 4-5 7-10 7-3 0-5 2-4 4 1 2 4 0 2-2l-3-3" />
          </>
        ) : null}
        {name === 'table' ? (
          <>
            <rect height="16" rx="1" width="18" x="3" y="4" />
            <path d="M3 9h18M9 9v11M15 9v11" />
          </>
        ) : null}
        {name === 'trash' ? (
          <>
            <path d="M5 7h14M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" />
          </>
        ) : null}
        {name === 'upload' ? (
          <>
            <path d="M12 16V3m0 0L7 8m5-5 5 5" />
            <path d="M4 14v6h16v-6" />
          </>
        ) : null}
        {name === 'zoom' ? (
          <>
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="m15.5 15.5 5 5M10.5 7.5v6M7.5 10.5h6" />
          </>
        ) : null}
      </g>
    </svg>
  );
}
